<script lang="ts">
    import Wrapper from '../utils/Wrapper.svelte';
    import { twMerge } from 'tailwind-merge';
    import type { InputType } from '../types';

    export let type: InputType = 'text';
    export let value: any = undefined;
    export let defaultClass = 'block border w-full';
    export let size: 'sm' | 'base' | 'lg' = 'base';
    export let color: 'base' | 'green' | 'red' = 'base';
    export let floatClass = 'flex absolute inset-y-0 items-center text-gray-500 dark:text-gray-400';

    const focusedClasses = 'focus:shadow-none focus:outline-0 outline-none';
    const disabledClasses = 'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400';

    const borderClasses = {
        base: 'border-gray-200 dark:border-gray-600',
        green: 'border-green-500 dark:border-green-500',
        red: 'border-red-400 dark:border-red-400',
    };

    const ringClasses = {
        base: 'focus:border-gray-600 focus:ring-gray-600 dark:focus:border-gray-600 dark:focus:ring-gray-600',
        green: 'focus:ring-green-500 focus:border-green-500 dark:focus:border-green-500 dark:focus:ring-green-500',
        red: 'focus:ring-red-400 focus:border-red-400 dark:focus:ring-red-400 dark:focus:border-red-400',
    };

    const sizeClasses = {
        sm: 'px-[0.75rem] py-[0.5rem] h-9 text-sm rounded-[0.625rem]',
        base: 'px-[0.875rem] py-[0.75rem] h-12 text-sm rounded-xl',
        lg: 'p-4 h-14 text-base rounded-xl',
    };

    // you need to this to avoid 2-way binding
    const setType = (node: HTMLInputElement, _type: string) => {
        node.type = _type;
        return {
            update(_type: string) {
                node.type = _type;
            },
        };
    };

    let inputClass: string;
    $: {
        inputClass = twMerge([
            defaultClass,
            focusedClasses,
            sizeClasses[size],
            $$slots.left && 'pl-[2.625rem]',
            $$slots.right && value && 'pr-[2.625rem]',
            ringClasses[color],
            'bg-white text-gray-900 placeholder:text-gray-400 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400',
            borderClasses[color],
            disabledClasses,
            $$props.class,
        ]);
    }
</script>

<Wrapper class="relative w-full" show={$$slots.left || $$slots.right}>
    {#if $$slots.left}
        <div class={twMerge(floatClass, 'pointer-events-none left-0 pl-4', $$props.classLeft)}>
            <slot name="left" />
        </div>
    {/if}
    <slot props={{ ...$$restProps, class: inputClass }}>
        <input
            {...$$restProps}
            bind:value
            on:blur
            on:change
            on:click
            on:contextmenu
            on:focus
            on:keydown
            on:keypress
            on:keyup
            on:mouseover
            on:mouseenter
            on:mouseleave
            on:paste
            on:input
            use:setType={type}
            class={inputClass}
        />
    </slot>
    {#if $$slots.right}
        <div class={twMerge(floatClass, 'right-0 pr-4', $$props.classRight)}><slot name="right" /></div>
    {/if}
</Wrapper>

<!--
  @component
  ## Feature
  [Go to Input Fields](https://flowbite-svelte.com/docs/forms/input-field)
  - Setup
  - Input fields
  - Disabled state
  - Validation
  - Input with icon
  - Icon click handler
  - Helper text
  - Number input
  - Advanced usage
  ## Props
  @prop type: InputType = 'text';
  @prop value: any = '';
  @prop defaultClass: string = 'block w-full disabled:cursor-not-allowed disabled:opacity-50';
  @prop color: 'base' | 'green' | 'red' = 'base';
  ## Event
  - on:blur
  - on:change
  - on:click
  - on:contextmenu
  - on:focus
  - on:keydown
  - on:keypress
  - on:keyup
  - on:mouseover
  - on:mouseenter
  - on:mouseleave
  - on:paste
  - on:input
  ## Example
  ```
  <form>
    <div class="grid gap-6 mb-6 md:grid-cols-2">
      <div>
        <Label for="first_name" class="mb-2">First name</Label>
        <Input type="text" id="first_name" placeholder="John" required  />
      </div>
      <div>
        <Label for="last_name" class="mb-2">Last name</Label>
        <Input type="text" id="last_name" placeholder="Doe" required />
      </div>
      <div>
        <Label for="company" class="mb-2">Company</Label>
        <Input type="text" id="company" placeholder="Flowbite" required />
      </div>
    </div>
    <Button type="submit">Submit</Button>
  </form>
  ```
-->
